/**
 * Image export utilities for MJJ Trading Tool
 * Requirements: 9.1, 9.2, 9.3
 */

/**
 * 動態載入 html2canvas
 */
async function loadHtml2Canvas(): Promise<typeof import('html2canvas')['default']> {
  const module = await import('html2canvas');
  return module.default;
}

/**
 * 生成檔案名稱
 * @param productName - 產品名稱
 * @returns 格式化的檔案名稱
 */
function generateFileName(productName: string): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const safeName = productName.replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '_').slice(0, 30);
  return `${safeName}_${timestamp}.png`;
}

/**
 * 下載卡片圖片
 * Property: 圖片導出功能
 * Validates: Requirements 9.1, 9.2, 9.3
 *
 * @param element - 要渲染的 DOM 元素
 * @param productName - 產品名稱（用於檔案名稱）
 * @returns Promise<void>
 */
export async function downloadCardImage(
  element: HTMLElement,
  productName: string
): Promise<void> {
  try {
    const html2canvas = await loadHtml2Canvas();

    // 使用 3 倍縮放以確保清晰度
    const canvas = await html2canvas(element, {
      scale: 3,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
    });

    // 轉換為 PNG 並下載
    const dataUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = generateFileName(productName);
    link.href = dataUrl;
    link.click();
  } catch (error) {
    console.error('Failed to export image:', error);
    throw new Error('圖片導出失敗，請稍後再試');
  }
}

/**
 * 將卡片渲染為 Blob
 * @param element - 要渲染的 DOM 元素
 * @returns Promise<Blob>
 */
export async function renderCardToBlob(element: HTMLElement): Promise<Blob> {
  const html2canvas = await loadHtml2Canvas();

  const canvas = await html2canvas(element, {
    scale: 3,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false,
  });

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob: Blob | null) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Failed to create blob'));
      }
    }, 'image/png');
  });
}
